import {twMerge} from "tailwind-merge";
import {
    AreaGraphWidgetComponent,
    BarGraphWidgetComponent,
    ComboChartWidgetComponent,
    DonutChartWidgetComponent,
    GaugeWidgetComponent,
    HorizontalBarGraphWidgetComponent,
    LineGraphWidgetComponent,
    NumberTileWidgetComponent,
    PieChartWidgetComponent,
    ProgressBarWidgetComponent,
    StackedBarGraphWidgetComponent,
    TableWidgetComponent,
    TextWidgetComponent,
} from "~/components/reports/widgetSpecific/widgetRenderers";
import type {Widget} from "~/components/reports/widgetSpecific/widgets";
import {WidgetType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import type {
    EditableReportAction,
    EditableReportState,
} from "~/modules/reports/state/editableReportState";

export type EditableWidgetComponentProps = {
    widget: Widget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    editableReportState: EditableReportState;
    editableReportDispatch: React.Dispatch<EditableReportAction>;
    pageIndex: number;
};

export function MultiBarGraphEditableWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    editableReportState,
    editableReportDispatch,
    pageIndex,
}: EditableWidgetComponentProps) {
    if (widget.type != WidgetType.multiBarGraph) {
        throw new Error("27ff2253-faca-4da8-99b8-c415d67c93aa");
    }

    return (
        <BarGraphWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            hideKebabMenu={true}
            barGraphType="default"
        />
    );
}

export function StackedBarGraphEditableWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    editableReportState,
    editableReportDispatch,
    pageIndex,
}: EditableWidgetComponentProps) {
    if (widget.type != WidgetType.stackedBarGraph) {
        throw new Error("695f2169-1a84-4d78-af5e-2aabb5f2024e");
    }

    return (
        <StackedBarGraphWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            hideKebabMenu={true}
            barGraphType="stacked"
        />
    );
}

export function HorizontalBarGraphEditableWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    editableReportState,
    editableReportDispatch,
    pageIndex,
}: EditableWidgetComponentProps) {
    if (widget.type != WidgetType.horizontalBarGraph) {
        throw new Error("7f70bde7-cd93-46d3-8a20-a48e58ef9851");
    }

    return (
        <HorizontalBarGraphWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            hideKebabMenu={true}
            barGraphType="default"
        />
    );
}

export function AreaGraphEditableWidgetComponent({
    widget,
    widgetWidth,
    widgetHeight,
    className,
    scalingFactor,
    editableReportState,
    editableReportDispatch,
    pageIndex,
}: EditableWidgetComponentProps) {
    if (widget.type !== WidgetType.areaGraph) {
        throw new Error("981e286b-dcfe-49a5-b878-bcb8e3843607");
    }

    return (
        <AreaGraphWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            hideKebabMenu={true}
        />
    );
}

export function ComboChartEditableWidgetComponent({
    widget,
    widgetWidth,
    widgetHeight,
    className,
    scalingFactor,
    editableReportState,
    editableReportDispatch,
    pageIndex,
}: EditableWidgetComponentProps) {
    if (widget.type !== WidgetType.comboChart) {
        throw new Error("b67c218c-b4a6-4fb7-bb60-a636bee8a36e");
    }

    return (
        <ComboChartWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            hideKebabMenu={true}
        />
    );
}

export function LineGraphEditableWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    editableReportState,
    editableReportDispatch,
    pageIndex,
}: EditableWidgetComponentProps) {
    if (widget.type != WidgetType.lineGraph) {
        throw new Error("7a833357-25e2-4e38-9329-fdeddc22fbdf");
    }

    return (
        <LineGraphWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            hideKebabMenu={true}
        />
    );
}

export function NumberTileEditableWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    editableReportState,
    editableReportDispatch,
    pageIndex,
}: EditableWidgetComponentProps) {
    if (widget.type != WidgetType.numberTile) {
        throw new Error("984cd812-6ead-4f84-a64b-97d7400fd8a3");
    }

    const fontSizeMultiplier = Number.isNaN(parseFloat(widget.fontSizeMultiplier)) ? 1 : parseFloat(widget.fontSizeMultiplier);

    return (
        <NumberTileWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            hideKebabMenu={true}
        />
    );
}

export function PieChartEditableWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    editableReportState,
    editableReportDispatch,
    pageIndex,
}: EditableWidgetComponentProps) {
    if (widget.type != WidgetType.pieChart) {
        throw new Error("db9f848d-1165-49e8-b1ba-fd7975f70832");
    }

    return (
        <PieChartWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            hideKebabMenu={true}
        />
    );
}

export function DonutChartEditableWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    editableReportState,
    editableReportDispatch,
    pageIndex,
}: EditableWidgetComponentProps) {
    if (widget.type != WidgetType.donutChart) {
        throw new Error("23e74d88-c203-4325-99c1-9f9ba517e0ce");
    }

    return (
        <DonutChartWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            hideKebabMenu={true}
        />
    );
}

export function TextEditableWidgetComponent({
    widget,
    widgetWidth,
    widgetHeight,
    className,
    scalingFactor,
    editableReportDispatch,
    editableReportState,
    pageIndex,
}: EditableWidgetComponentProps) {
    if (widget.type != WidgetType.text) {
        throw new Error("c000dc32-b635-4198-8213-adba578d0503");
    }

    return (
        <TextWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
        />
    );
}

export function ImageEditableWidgetComponent({
    pageIndex,
    editableReportDispatch,
    editableReportState,
    scalingFactor,
    widget,
    widgetHeight,
    widgetWidth,
    className,
}: EditableWidgetComponentProps) {
    if (widget.type != WidgetType.image) {
        throw new Error("a899fbaf-96ab-48e0-a237-e5ac14de4dee");
    }

    return <div className={twMerge("p-4", className)}>Image</div>;
}

export function TableEditableWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    editableReportState,
    editableReportDispatch,
    pageIndex,
}: EditableWidgetComponentProps) {
    if (widget.type != WidgetType.table) {
        throw new Error("4cf26b16-4d96-4e80-8f4f-d03f36d33abc");
    }

    return (
        <TableWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            hideKebabMenu={true}
        />
    );
}

export function ProgressBarEditableWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    editableReportState,
    editableReportDispatch,
    pageIndex,
}: EditableWidgetComponentProps) {
    if (widget.type != WidgetType.progressBar) {
        throw new Error("7426ea3b-d6fa-4884-b6fd-144f53ce9f6a");
    }

    return (
        <ProgressBarWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            hideKebabMenu={true}
        />
    );
}

export function GaugeEditableWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    editableReportState,
    editableReportDispatch,
    pageIndex,
}: EditableWidgetComponentProps) {
    if (widget.type != WidgetType.gauge) {
        throw new Error("349f7024-2576-49e8-bff0-281548329eb7");
    }

    return (
        <GaugeWidgetComponent
            widget={widget}
            widgetHeight={widgetHeight}
            widgetWidth={widgetWidth}
            scalingFactor={scalingFactor}
            hideKebabMenu={true}
        />
    );
}
